import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Controller, useForm } from "react-hook-form";
import * as z from "zod";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { setActiveOrgId } from "@/lib/activeOrg.ts";

// FRE-272: minimal create-org flow.
//
// We POST directly to Better Auth's organization plugin endpoint rather than
// adding a custom worker route — the plugin already handles slug uniqueness,
// permissions, and inserts the member row with the creator as owner. On
// success we set the new org as active and route home.

export const Route = createFileRoute("/orgs/new")({
  component: CreateOrgComponent,
});

// Slug derivation rule: lowercase, replace runs of non-alphanumerics with a
// single hyphen, strip leading/trailing hyphens. Matches Better Auth's slug
// regex (kebab-case ASCII) and gives a sensible default the user can edit.
function deriveSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "") // strip combining marks
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const formSchema = z.object({
  name: z.string().trim().min(1, "Name is required."),
  slug: z
    .string()
    .trim()
    .min(1, "Slug is required.")
    .regex(/^[a-z0-9-]+$/, "Lowercase letters, numbers, and hyphens only."),
});

type FormValues = z.infer<typeof formSchema>;

type CreateOrgResponse = { id: string; slug: string; name: string };

function CreateOrgComponent() {
  const navigate = useNavigate();
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: "", slug: "" },
  });
  const error = form.formState.errors.root?.message;

  // Auto-derive slug while the user hasn't manually edited it. Once they
  // touch the slug field we stop auto-syncing — otherwise we'd clobber
  // intentional edits on the next name keystroke. We detect "touched" via
  // rhf's `formState.dirtyFields.slug`.
  function handleNameChange(value: string) {
    form.setValue("name", value, { shouldValidate: true });
    if (!form.formState.dirtyFields.slug) {
      form.setValue("slug", deriveSlug(value), { shouldValidate: true });
    }
  }

  async function onSubmit(values: FormValues) {
    form.clearErrors("root");
    try {
      const res = await fetch("/api/auth/organization/create", {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          origin: window.location.origin,
        },
        body: JSON.stringify({ name: values.name, slug: values.slug }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as {
          message?: string;
          error?: string;
        } | null;
        form.setError("root", {
          message: body?.message ?? body?.error ?? `Create failed (${res.status})`,
        });
        return;
      }
      const created = (await res.json()) as CreateOrgResponse;
      // Set as active before navigating so the file-list loader picks up the
      // right org on first render.
      await setActiveOrgId(created.id);
      await navigate({ to: "/" });
    } catch (err) {
      form.setError("root", {
        message: err instanceof Error ? err.message : "Create failed",
      });
    }
  }

  return (
    <div className="mx-auto mt-12 max-w-sm px-6">
      <h1 className="mb-6 text-2xl font-semibold">Create a new org</h1>
      <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
        <FieldGroup>
          <Controller
            name="name"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="org-name">Name</FieldLabel>
                <Input
                  id="org-name"
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onChange={(e) => handleNameChange(e.target.value)}
                  type="text"
                  placeholder="Acme Inc"
                  autoComplete="organization"
                  aria-invalid={fieldState.invalid}
                />
                {fieldState.invalid && fieldState.error ? (
                  <FieldError errors={[fieldState.error]} />
                ) : null}
              </Field>
            )}
          />
          <Controller
            name="slug"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="org-slug">Slug</FieldLabel>
                <Input
                  {...field}
                  id="org-slug"
                  type="text"
                  placeholder="acme-inc"
                  className="font-mono text-sm"
                  aria-invalid={fieldState.invalid}
                />
                <FieldDescription>
                  Lowercase letters, numbers, and hyphens. Used in file URLs.
                </FieldDescription>
                {fieldState.invalid && fieldState.error ? (
                  <FieldError errors={[fieldState.error]} />
                ) : null}
              </Field>
            )}
          />
          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <Button type="submit" disabled={form.formState.isSubmitting} className="w-full">
            {form.formState.isSubmitting ? "Creating…" : "Create org"}
          </Button>
        </FieldGroup>
      </form>
    </div>
  );
}
